/* eslint-disable no-unused-vars */
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PackageSearchControls from '../components/package_manager/PackageSearchControls';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import zoeShipmentTrackingLogService from '../services/zoe-shipment-tracking-log-service';

export default function ZoeShipmentTrackingLogPage() {
  const [logs, setLogs] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });

  // restore saved search state
  useEffect(() => {
    const savedSearch = localStorage.getItem('zoeSearchValue');
    const savedFilters = localStorage.getItem('zoeAdvancedSearchFilters');
    if (savedSearch) setSearchValue(savedSearch);
    if (savedFilters) setFilters(JSON.parse(savedFilters));
  }, []);

  const fetchAllLogs = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await zoeShipmentTrackingLogService.getList(
          page,
          limit
        );
        if (response.success) {
          setLogs(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setLogs([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to fetch shipments:', err);
      }
      setLoading(false);
    },
    [pagination.limit]
  );

  useEffect(() => {
    fetchAllLogs();
  }, [fetchAllLogs]);

  useEffect(() => {
    localStorage.setItem('zoeSearchValue', searchValue);
  }, [searchValue]);

  useEffect(() => {
    localStorage.setItem('zoeAdvancedSearchFilters', JSON.stringify(filters));
  }, [filters]);

  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const filterObj = {};
        filters.forEach((f) => {
          if (f.field && f.value) filterObj[f.field] = f.value;
        });
        if (dateFilter !== 'default') {
          filterObj.date_filter = dateFilter;
        }
        filterObj.TrackingNumber = searchValue;

        const response = await zoeShipmentTrackingLogService.searchByListField(
          filterObj,
          page,
          sortBy !== 'default' ? sortBy : undefined,
          sortOrder !== 'default' ? sortOrder : undefined,
          limit
        );

        if (response.success) {
          setLogs(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setLogs([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to search shipments:', err);
      }
      setLoading(false);
    },
    [searchValue, filters, dateFilter, sortBy, sortOrder, pagination.limit]
  );

  const debouncedSearch = useMemo(
    () => debounce((page, limit) => handleSearch(page, limit), 500),
    [handleSearch]
  );

  useEffect(() => {
    if (
      searchValue ||
      filters.length > 0 ||
      dateFilter !== 'default' ||
      sortBy !== 'default' ||
      sortOrder !== 'default'
    ) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      debouncedSearch(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'TrackingNumber':
        return 'Tracking Number';
      case 'Source':
        return 'Source';
      case 'InputType':
        return 'Input Type';
      case 'UserId':
        return 'User Id';
      default:
        return field;
    }
  };

  const filterOptions = [
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'Source', label: 'Source' },
    { value: 'InputType', label: 'Input Type' },
    { value: 'UserId', label: 'User Id' }
  ];

  const sortByList = [
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'Source', label: 'Source' },
    { value: 'InputType', label: 'Input Type' },
    { value: 'UserId', label: 'User Id' },
    { value: 'CollectedDate', label: 'Collected Date' }
  ];

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Zoe Shipment Tracking Logs Page
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>
          <PackageSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            fetchAllPackages={fetchAllLogs}
            setPagination={setPagination}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={logs.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAllPackages={fetchAllLogs}
          />

          {loading ? (
            <p>Loading...</p>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Collected Date</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>InputType</TableCell>
                    <TableCell>UserId</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.Id}</TableCell>
                      <TableCell>{s.TrackingNumber}</TableCell>
                      <TableCell>{s.CollectedDate}</TableCell>
                      <TableCell>{s.Source}</TableCell>
                      <TableCell>{s.InputType}</TableCell>
                      <TableCell>{s.UserId}</TableCell>
                      <TableCell>{s.Note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={logs.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAllPackages={fetchAllLogs}
          />
        </div>
      </div>
    </div>
  );
}
